/* eslint-disable */
import axios from 'axios';
import {
  GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT_URL,
  GET_APPLICATION_RISK_HISTORY_URL,
  GET_RISK_CHECKLIST_WEIGHTS_URL,
  GET_SUBMITTED_RISK_ASSESSMENT_ANSWERS_URL,
  LIST_RISK_FRAMEWORKS_BY_PROJECT_URL,
  SUBMIT_RISK_ASSESSMENT_ANSWERS_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';
import { extract_items, first_value, parse_response_data } from '@/services/onboarding_data_helpers';
import { onboarding_request_cache } from '@/services/onboarding_journey_service_helpers';
import {
  apply_embedded_risk_result,
  apply_latest_risk_result,
  case_has_embedded_risk_result,
  collect_current_form_answer_map,
  get_application_answer_map,
  get_configured_assessments,
  is_child_onboarding_case,
  normalize_framework_items,
  normalize_risk_level,
  normalize_submission_result,
  resolve_case_context,
  risk_debug,
  risk_tone_from_level
} from '@/services/onboarding_risk_service_helpers';

export { risk_tone_from_level };

export async function fetch_risk_frameworks_by_project(project_id = null) {
  const response = await axios.get(
    LIST_RISK_FRAMEWORKS_BY_PROJECT_URL,
    get_request_config({ params: project_id ? { project_id } : {} })
  );

  return normalize_framework_items(response);
}

export async function fetch_assigned_questions_to_assessment(assessment_id) {
  if (!assessment_id) return [];

  const response = await axios.get(
    GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT_URL,
    get_request_config({ params: { assessment_id } })
  );

  const questions = extract_items(response).map((question) => ({
    ...question,
    question_id: Number(first_value([question?.question, question?.question_id, question?.id], 0)) || null,
    choices: Array.isArray(question?.choices)
      ? question.choices.map((choice) => ({
          option: typeof choice === 'string' ? choice : String(choice?.option || '').trim()
        })).filter((choice) => choice.option)
      : []
  })).filter((question) => question.question_id);
  risk_debug('assigned questions loaded', { assessment_id, questions });
  return questions;
}

export async function fetch_risk_checklist_weights(assessment_id) {
  if (!assessment_id) return [];

  const response = await axios.get(
    GET_RISK_CHECKLIST_WEIGHTS_URL,
    get_request_config({ params: { assessment_id } })
  );

  return extract_items(response);
}

export async function fetch_submitted_risk_assessment({ entity_id, assessment_id } = {}) {
  if (!has_risk_assessment_scope(entity_id, assessment_id)) return null;

  const response = await axios.get(
    GET_SUBMITTED_RISK_ASSESSMENT_ANSWERS_URL,
    get_request_config({ params: { entity_id, assessment_id } })
  ).catch((error) => {
    if ([404, 500].includes(Number(error?.response?.status))) return null;
    throw error;
  });

  return response ? parse_response_data(response)?.data || null : null;
}

export async function fetch_application_risk_history({ application_id, entity_id = null } = {}) {
  if (!application_id) return [];

  const params = entity_id ? { application_id, entity_id } : { application_id };
  const response = await axios.get(
    GET_APPLICATION_RISK_HISTORY_URL,
    get_request_config({ params })
  );

  return extract_items(response);
}

export async function submit_risk_assessment_answers({ entity_id, assessment_id, answers, application_id = null } = {}) {
  if (!has_risk_submission_scope({ entity_id, assessment_id, answers })) return null;

  const response = await axios.post(
    SUBMIT_RISK_ASSESSMENT_ANSWERS_URL,
    { entity_id, assessment_id, application_id, answers },
    get_request_config()
  );

  onboarding_request_cache.all_cases.clear();
  onboarding_request_cache.onboarding_status.clear();
  return parse_response_data(response)?.data || null;
}

function has_answers(answers) {
  return Boolean(answers && Object.keys(answers).length);
}

function has_risk_assessment_scope(entity_id, assessment_id) {
  return Boolean(entity_id && assessment_id);
}

function has_risk_submission_scope({ entity_id, assessment_id, answers }) {
  return has_risk_assessment_scope(entity_id, assessment_id) && has_answers(answers);
}

async function map_submitted_assessment_result(assessment, entity_id) {
  const result = await fetch_submitted_risk_assessment({
    entity_id,
    assessment_id: assessment.id
  }).catch(() => null);

  return result
    ? { ...result, assessment_id: assessment.id, assessment_name: assessment.name }
    : null;
}

function build_assessment_answers({ assessment_questions = [], answer_map }) {
  const answers = {};

  assessment_questions.forEach((question) => {
    const answer = answer_map.get(question.question_id);
    const valid_answers = get_valid_question_answers(question, answer);
    if (valid_answers.length) {
      Reflect.set(answers, String(question.question_id), Array.isArray(answer) ? valid_answers : valid_answers[0]);
    }
  });

  return answers;
}

function get_valid_question_answers(question, answer) {
  if (!answer) return [];

  const allowed_options = new Set(question.choices.map((choice) => String(choice?.option || '').trim()));
  const selected_answers = Array.isArray(answer) ? answer : [answer];
  const valid_answers = selected_answers.filter((selected) => allowed_options.has(String(selected || '').trim()));
  risk_debug('question answer matched', {
    question_id: question.question_id,
    submitted_answer: answer,
    allowed_options: Array.from(allowed_options),
    valid_answers
  });
  return valid_answers;
}

async function build_assessment_question_groups(assessments = []) {
  return Promise.all(
    assessments.map(async (assessment) => ({
      assessment,
      questions: await fetch_assigned_questions_to_assessment(assessment.id)
    }))
  );
}

function has_current_form_risk_answer(assessment_question_groups, current_answer_map) {
  return assessment_question_groups.some(({ questions }) => (
    questions.some((question) => current_answer_map.has(question.question_id))
  ));
}

function merge_current_answers(answer_map, current_answer_map) {
  current_answer_map.forEach((answer, question_id) => {
    answer_map.set(question_id, answer);
  });
  return answer_map;
}

function assert_assessment_can_submit(assessment, questions, answers) {
  const assessment_name = String(assessment.name || 'Risk assessment');
  // const question_id_list = questions.map((question) => question.question_id).filter(Boolean).join(', ');
  if (!questions.length) {
    throw new Error(`Risk assessment "${assessment_name}" has no configured questions.`);
  }
  // if (!has_answers(answers)) {
  //   throw new Error(
  //     `Risk assessment "${assessment_name}" could not match submitted answers for question IDs: ${question_id_list}.`
  //   );
  // }
}

async function submit_assessment_group({ assessment, questions }, context, answer_map) {
  const answers = build_assessment_answers({ assessment_questions: questions, answer_map });
  assert_assessment_can_submit(assessment, questions, answers);

  const result = await submit_risk_assessment_answers({
    entity_id: context.entity_profile_id,
    assessment_id: assessment.id,
    application_id: context.application_id,
    answers
  });
  risk_debug('assessment submitted', {
    entity_id: context.entity_profile_id,
    assessment_id: assessment.id,
    answers,
    result
  });
  return result ? normalize_submission_result(result, assessment) : null;
}

export async function submit_application_risk_assessments({
  application_id,
  entity_profile_id = null,
  entity_type_id = null,
  project_id = null,
  selected_form = {},
  current_form_sections = [],
  current_form_values = {}
} = {}) {
  const context = await resolve_case_context({
    application_id,
    entity_profile_id,
    entity_type_id,
    project_id,
    selected_form
  });
  if (!context.entity_profile_id) {
    risk_debug('submission skipped: entity profile missing', { application_id, selected_form });
    return [];
  }

  const assessments = await get_configured_assessments(context);
  if (!assessments.length) {
    risk_debug('submission skipped: no configured assessments', context);
    return [];
  }

  const assessment_question_groups = await build_assessment_question_groups(assessments);
  const current_answer_map = collect_current_form_answer_map(current_form_sections, current_form_values);
  if (!has_current_form_risk_answer(assessment_question_groups, current_answer_map)) {
    risk_debug('submission skipped: current form has no risk answers', {
      application_id,
      assessment_ids: assessments.map((assessment) => assessment.id)
    });
    return [];
  }

  const answer_map = merge_current_answers(await get_application_answer_map(application_id), current_answer_map);
  risk_debug('form answers collected', {
    application_id,
    answers: Object.fromEntries(answer_map.entries())
  });

  const results = await Promise.all(
    assessment_question_groups.map((group) => submit_assessment_group(group, context, answer_map))
  );
  return results.filter(Boolean);
}

export async function hydrate_case_risk_result(case_item = {}) {
  if (is_child_onboarding_case(case_item)) return case_item;
  if (case_has_embedded_risk_result(case_item)) return apply_embedded_risk_result(case_item);

  const entity_id = Number(case_item?.entity_profile_id || 0);
  if (!entity_id) return case_item;

  const latest_result = await get_latest_submitted_result_for_case(case_item, entity_id);
  if (!latest_result) return case_item;

  const risk_level = normalize_risk_level(latest_result.risk_level);
  return apply_latest_risk_result(case_item, latest_result, risk_level);
}

async function get_latest_submitted_result_for_case(case_item, entity_id) {
  const started_assessments = await get_started_assessments_for_case(case_item, entity_id);
  if (!started_assessments.length) return null;

  const submitted_results = await get_submitted_assessment_results(started_assessments, entity_id);
  risk_debug('queue risk hydration completed', {
    application_id: case_item?.application_id,
    entity_id,
    assessments: started_assessments,
    submitted_results
  });
  return submitted_results.find(Boolean) || null;
}

async function get_started_assessments_for_case(case_item, entity_id) {
  const assessments = await get_configured_assessments({
    application_id: Number(case_item?.application_id || 0),
    entity_profile_id: entity_id,
    entity_type_id: Number(case_item?.entity_type_id || 0),
    project_id: Number(case_item?.project_id || 0)
  }).catch(() => []);

  return assessments.filter((assessment) => assessment?.is_started || assessment?.subject_id);
}

async function get_submitted_assessment_results(started_assessments, entity_id) {
  return Promise.all(
    started_assessments.map((assessment) => map_submitted_assessment_result(assessment, entity_id))
  );
}
